/** Work order execution panels: parts (plan/issue/return), failure report, meter reading. */
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { useAsset } from "@/features/assets/queries";
import {
  PartCommitmentActions,
  PartCommitmentSummary,
  PartStockLocationSelect,
} from "@/features/inventory/PartCommitments";
import { ProcurementPanel } from "@/features/inventory/ProcurementPanel";
import {
  type FailureReport,
  type Part,
  type WorkOrderDetail,
  useAddPart,
  usePartMovement,
  useRecordReading,
  useSetFailureReport,
} from "@/features/workorders/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { ItemName } from "@/shared/components/item-name";
import { EmptyState, StatusBadge } from "@/shared/components/page";
import { ItemPicker } from "@/shared/components/pickers";
import { formatQuantity } from "@/shared/lib/format";
import { isoOrNull, nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Table, Td, Th, Tr } from "@/shared/ui/primitives";

interface PartValues extends Record<string, unknown> {
  item_id: string;
  quantity: string;
  stock_location_id: string;
}

/** Dialog to plan a part on the work order. */
export function AddPartDialog({
  wo,
  onClose,
}: {
  wo: WorkOrderDetail;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const add = useAddPart(wo.id);
  const form = useForm<PartValues>({
    initial: { item_id: "", quantity: "1", stock_location_id: "" },
    schema: z.object({
      item_id: z.string().min(1, t("app.required")),
      quantity: z.coerce.number().positive(t("app.required")),
    }),
    idPrefix: "part",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      add.mutateAsync({
        item_id: v.item_id,
        quantity: Number(v.quantity),
        stock_location_id: nullable(v.stock_location_id),
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("workOrders.addPart")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      submitLabel={t("app.add")}
    >
      <Field id="part-item_id" label={t("inventory.title")} error={form.errors.item_id}>
        <ItemPicker
          id="part-item_id"
          value={form.values.item_id}
          onChange={(id) => form.set("item_id", id ?? "")}
          required
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="part-quantity" label={t("app.quantity")} error={form.errors.quantity}>
          <Input type="number" step="any" min="0" {...form.field("quantity")} />
        </Field>
        <Field
          id="part-stock_location_id"
          label={t("workOrders.fromStock")}
          hint={t("workOrders.fromStockHint")}
        >
          <PartStockLocationSelect
            id="part-stock_location_id"
            itemId={form.values.item_id}
            siteId={wo.site?.id ?? null}
            value={form.values.stock_location_id}
            onChange={(id) => form.set("stock_location_id", id)}
          />
        </Field>
      </div>
    </FormDialog>
  );
}

/** Dialog to issue or return a planned part. */
export function PartMovementDialog({
  wo,
  part,
  action,
  onClose,
}: {
  wo: WorkOrderDetail;
  part: Part;
  action: "issue" | "return";
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const move = usePartMovement(wo.id);
  const form = useForm<{ quantity: string; stock_location_id: string }>({
    initial: { quantity: part.quantity, stock_location_id: part.stock_location_id ?? "" },
    schema: z.object({
      quantity: z.coerce.number().positive(t("app.required")),
      stock_location_id: action === "issue" ? z.string().min(1, t("app.required")) : z.string(),
    }),
    idPrefix: "move",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      move.mutateAsync({
        partId: part.id,
        action,
        body: {
          quantity: Number(v.quantity),
          ...(action === "issue" ? { stock_location_id: v.stock_location_id } : {}),
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t(action === "issue" ? "workOrders.issuePart" : "workOrders.returnPart")}
      description={`${part.item_sku} · ${part.item_name}`}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      submitLabel={t(action === "issue" ? "workOrders.issue" : "workOrders.return")}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="move-quantity" label={t("app.quantity")} error={form.errors.quantity}>
          <Input type="number" step="any" min="0" {...form.field("quantity")} />
        </Field>
        {action === "issue" ? (
          <Field
            id="move-stock_location_id"
            label={t("inventory.location")}
            error={form.errors.stock_location_id}
          >
            <PartStockLocationSelect
              id="move-stock_location_id"
              itemId={part.item_id}
              siteId={wo.site?.id ?? null}
              value={form.values.stock_location_id}
              onChange={(id) => form.set("stock_location_id", id)}
              required
            />
          </Field>
        ) : null}
      </div>
    </FormDialog>
  );
}

/** Parts tab content. */
export function PartsPanel({ wo }: { wo: WorkOrderDetail }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [dialog, setDialog] = useState<
    { kind: "add" } | { kind: "move"; part: Part; action: "issue" | "return" } | null
  >(null);
  const open = !["completed", "closed", "cancelled", "verified"].includes(wo.status);
  const canRequest = open && can("workorders.parts.request");
  const canIssue = open && can("workorders.parts.issue");
  const canReturn =
    !["closed", "cancelled", "verified"].includes(wo.status) && can("workorders.parts.issue");
  const canAct = canRequest || canIssue || canReturn || (open && can("procurement.request.create"));
  return (
    <div className="space-y-3">
      {dialog?.kind === "add" ? <AddPartDialog wo={wo} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "move" ? (
        <PartMovementDialog
          wo={wo}
          part={dialog.part}
          action={dialog.action}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {canRequest ? (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "add" })}>
            {t("workOrders.addPart")}
          </Button>
        </div>
      ) : null}
      {wo.parts.length === 0 ? (
        <EmptyState message={t("workOrders.noParts")} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t("inventory.sku")}</Th>
              <Th>{t("app.name")}</Th>
              <Th>{t("app.quantity")}</Th>
              <Th>{t("app.status")}</Th>
              {can("workorders.cost.view") ? <Th>{t("workOrders.costs")}</Th> : null}
              {canAct ? <Th /> : null}
            </tr>
          </thead>
          <tbody>
            {wo.parts.map((p) => (
              <Tr key={p.id}>
                <Td className="font-mono text-xs">{p.item_sku}</Td>
                <Td>
                  <ItemName itemId={p.item_id} name={p.item_name} sku={p.item_sku} />
                </Td>
                <Td className="tabular-nums">
                  {formatQuantity(p.quantity)} {p.uom}
                  <PartCommitmentSummary part={p} />
                </Td>
                <Td>
                  <StatusBadge value={p.status} namespace="workOrders.partStatus" />
                </Td>
                {can("workorders.cost.view") ? (
                  <Td className="tabular-nums">
                    {formatQuantity(p.total_cost)}{" "}
                    {p.currency === "" ? t("workOrders.partCurrencyUnknown") : p.currency}
                  </Td>
                ) : null}
                {canAct ? (
                  <Td className="text-end">
                    <PartCommitmentActions wo={wo} part={p} />
                    {canIssue && (p.status === "planned" || p.status === "reserved") ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: "move", part: p, action: "issue" })}
                      >
                        {t("workOrders.issue")}
                      </Button>
                    ) : null}
                    {canReturn && p.status === "issued" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ kind: "move", part: p, action: "return" })}
                      >
                        {t("workOrders.return")}
                      </Button>
                    ) : null}
                  </Td>
                ) : null}
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {can("procurement.request.view") ? <ProcurementPanel workOrderId={wo.id} /> : null}
    </div>
  );
}

interface FailureValues extends Record<string, unknown> {
  problem_code: string;
  cause_code: string;
  remedy_code: string;
  narrative: string;
  safety_related: boolean;
  failed_at: string;
}

const FAILURE_CODES = {
  problem_code: [
    "overheating",
    "leak",
    "noise",
    "vibration",
    "no_start",
    "electrical",
    "corrosion",
    "wear",
  ],
  cause_code: [
    "wear",
    "fatigue",
    "contamination",
    "operator_error",
    "design",
    "lack_of_maintenance",
    "environment",
  ],
  remedy_code: [
    "replaced",
    "repaired",
    "adjusted",
    "cleaned",
    "lubricated",
    "reprogrammed",
    "no_fault_found",
  ],
} as const;

/** Complete code choices with an explicit free-text mode for tenant-specific codes. */
function FailureCodeField({
  id,
  label,
  value,
  options,
  onChange,
  disabled,
  error,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  disabled: boolean;
  error?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [customSelected, setCustomSelected] = useState(false);
  const custom = customSelected || (value !== "" && !options.includes(value));
  const customLabel = t("workOrders.customCode", { field: label });
  return (
    <div className="min-w-0 space-y-2">
      <Field id={`${id}-choice`} label={label} error={custom ? undefined : error}>
        <Select
          id={`${id}-choice`}
          value={custom ? "__custom__" : value}
          onChange={(event) => {
            const next = event.target.value;
            setCustomSelected(next === "__custom__");
            onChange(next === "__custom__" ? "" : next);
          }}
          disabled={disabled}
          aria-invalid={!custom && error ? true : undefined}
        >
          <option value="">—</option>
          {options.map((code) => (
            <option key={code} value={code}>
              {t(`workOrders.failureCodes.${code}`)}
            </option>
          ))}
          <option value="__custom__">{customLabel}</option>
        </Select>
      </Field>
      {custom ? (
        <Field id={id} label={customLabel} error={error}>
          <Input
            id={id}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
          />
        </Field>
      ) : null}
    </div>
  );
}

/** Failure report form (corrective work). */
export function FailureReportForm({
  wo,
  editable,
}: {
  wo: WorkOrderDetail;
  editable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const set = useSetFailureReport(wo.id);
  const fr: FailureReport | null = wo.failure_report;
  const initial = useMemo<FailureValues>(
    () => ({
      problem_code: fr?.problem_code ?? "",
      cause_code: fr?.cause_code ?? "",
      remedy_code: fr?.remedy_code ?? "",
      narrative: fr?.narrative ?? "",
      safety_related: fr?.safety_related ?? false,
      failed_at: fr?.failed_at?.slice(0, 16) ?? "",
    }),
    [fr],
  );
  const form = useForm<FailureValues>({ initial, idPrefix: "fr" });
  const submit = async (): Promise<void> => {
    await form.submit((v) =>
      set.mutateAsync({
        problem_code: v.problem_code,
        cause_code: v.cause_code,
        remedy_code: v.remedy_code,
        narrative: v.narrative,
        safety_related: v.safety_related,
        failed_at: isoOrNull(v.failed_at),
      }),
    );
  };
  return (
    <form
      className="space-y-3 rounded-md border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      noValidate
    >
      <h3 className="text-sm font-semibold">{t("workOrders.failureReport")}</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(
          [
            ["problem_code", t("workOrders.problemCode")],
            ["cause_code", t("workOrders.causeCode")],
            ["remedy_code", t("workOrders.remedyCode")],
          ] as const
        ).map(([key, label]) => (
          <FailureCodeField
            key={key}
            id={`fr-${key}`}
            label={label}
            value={form.values[key]}
            options={FAILURE_CODES[key]}
            onChange={(value) => form.set(key, value)}
            disabled={!editable || form.submitting}
            error={form.errors[key]}
          />
        ))}
      </div>
      <Field id="fr-narrative" label={t("workOrders.narrative")}>
        <Textarea {...form.field("narrative")} disabled={!editable} />
      </Field>
      <div className="flex flex-wrap items-end gap-4">
        <Field id="fr-failed_at" label={t("workOrders.failedAt")}>
          <Input type="datetime-local" {...form.field("failed_at")} disabled={!editable} />
        </Field>
        <Checkbox
          id="fr-safety_related"
          label={t("workOrders.safetyRelated")}
          checked={form.values.safety_related}
          onChange={(e) => form.set("safety_related", e.target.checked)}
          disabled={!editable}
        />
        {editable ? (
          <Button type="submit" size="sm" disabled={form.submitting || !form.dirty}>
            {t("app.save")}
          </Button>
        ) : null}
        {set.isSuccess && !form.dirty ? (
          <span className="text-xs text-emerald-700">{t("app.saved")}</span>
        ) : null}
      </div>
      {form.errors._ ? (
        <p role="alert" className="text-sm text-destructive">
          {form.errors._}
        </p>
      ) : null}
    </form>
  );
}

/** Record a reading for one of the asset's meters, linked to the work order. */
export function MeterReadingForm({ wo }: { wo: WorkOrderDetail }): ReactElement | null {
  const { t } = useTranslation();
  const { can } = useAuth();
  const asset = useAsset(wo.asset.id);
  const record = useRecordReading();
  const meters = asset.data?.meters ?? [];
  const [meterId, setMeterId] = useState("");
  const [value, setValue] = useState("");
  const selected = meters.find((m) => m.id === (meterId || meters[0]?.id));
  if (!can("meters.reading.record") || meters.length === 0) {
    return null;
  }
  const submit = (): void => {
    const target = meterId || meters[0]?.id;
    const n = numberOrNull(value);
    if (!target || n === null) {
      return;
    }
    record.mutate(
      {
        meterId: target,
        body: {
          value: n,
          source: "workorder",
          workorder_id: wo.id,
          read_at: null,
          source_ref: "",
          is_reset: false,
          note: "",
        },
      },
      { onSuccess: () => setValue("") },
    );
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
      <Field id="reading-meter" label={t("assets.meters")}>
        <Select
          id="reading-meter"
          className="w-56"
          value={meterId || (meters[0]?.id ?? "")}
          onChange={(e) => setMeterId(e.target.value)}
        >
          {meters.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({formatQuantity(m.current_value)} {m.unit})
            </option>
          ))}
        </Select>
      </Field>
      <Field id="reading-value" label={t("workOrders.newReading")}>
        <Input
          id="reading-value"
          type="number"
          step="any"
          className="w-32"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Field>
      <Button type="button" size="sm" onClick={submit} disabled={record.isPending || !value}>
        {t("workOrders.recordReading")}
      </Button>
      {selected?.unit ? (
        <span className="pb-2 text-xs text-muted-foreground">{selected.unit}</span>
      ) : null}
      {record.isError ? (
        <p role="alert" className="w-full text-sm text-destructive">
          {errorMessage(record.error)}
        </p>
      ) : null}
      {record.isSuccess ? (
        <p className="w-full text-xs text-emerald-700">{t("app.saved")}</p>
      ) : null}
    </div>
  );
}
